import React, { useMemo } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ChevronRight, Building2, Users } from 'lucide-react';
import { Tooltip } from 'antd';
import { useTeam } from '../../providers/TeamProvider';
import CircleProgress from '../../components/CircleProgress';
import User from '../../models/User';
import UserImageCircle, { UserImageCircleNEW } from '../../components/UserImageCircle';
import TalentDisplayName from '../../components/TalentDisplayName';
import TeamMatchDetails from './TeamMatchDetails';


const TeamTileHorizontalRedesign = (props) => {
    const { team, canFavorite, criteria, removeFromFavorites, addToFavorites } = useTeam();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const vips = TB.filter(team.topPositionsForDisplay(), vip => TB.isDefined(vip)).map(vip => User.make({ ...vip }));

    const teamProfileLink = useMemo(() => ({
        ...location,
        pathname: `team-profiles/${team.id}`
    }), [team.id, location]);

    const teamMemberLink = useMemo(() => ({
        state: {
            linkParams: {
                title: 'Back',
                path: location.pathname + (location.search || '')
            }
        }
    }), [location.pathname, location.search]);

    const memberProfilePath = (userId) => ({
        ...location,
        pathname: `${location.pathname}/team-profiles/${team.id}/talent-profiles/${userId}`,
        state: teamMemberLink.state
    });

    const showMatchDetails = () => {
        return TB.event('Modal@show', {
            modalClass: 'match-details-modal',
            content: (
                <TeamMatchDetails
                    team={team}
                    isEngagementLead={false}
                    criteria={criteria || {}}
                />
            )
        });
    };

    const isAgency = team.type === 'Agency' && team.supplier_client;
    if (isAgency) {
        console.log(team, 'TEAM')
    }

    return (
        <article className="relative bg-white border border-primary/15 rounded-[14px] shadow-sm hover:shadow-md overflow-hidden flex flex-col transition-shadow duration-150">
            {/* Accent bar */}
            <div className={`h-1 ${isAgency ? 'bg-[#6b6480]' : 'bg-primary'}`} />

            {/* Header */}
            <div className="min-h-[116px] px-6 pt-[18px] flex flex-col bg-white">
                <div className="flex justify-between items-start gap-4">
                    <div className="min-w-0 flex flex-col">
                        {/* Eyebrow */}
                        <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wide uppercase mb-[5px] w-max ${isAgency ? 'text-[#6b647f]' : 'text-primary'}`}>
                            {isAgency ? (
                                <>
                                    {/* <i className="fas fa-building text-xs" /> */}
                                    <Building2 className="h-3.5 w-3.5" />
                                </>
                            ) : (
                                <>
                                    {/* <i className="fas fa-users text-xs" /> */}
                                    <Users className="h-3.5 w-3.5" />
                                </>
                            )}
                            {isAgency ? 'Agency Team' : 'Freelance Collective'}
                        </span>
                        <h3 className="m-0 text-2xl font-bold tracking-tight leading-tight overflow-hidden text-ellipsis whitespace-nowrap">
                            {team.name}
                        </h3>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        {/* <Link to={teamProfileLink} className="btn btn-primary inline-flex items-center gap-[7px] text-sm font-bold no-underline">
                            View Team <ChevronRight className="h-4 w-4" />
                        </Link> */}

                        {canFavorite && (
                            <Tooltip
                                title={team.is_favorite ? 'Remove from Favorites' : 'Add to Favorites'}
                            >
                                <button
                                    className={TB.mapClass(
                                        'relative bg-white border rounded-full w-[34px] h-[34px] flex items-center justify-center cursor-pointer transition shrink-0 hover:bg-[#faf9fe]',
                                        {
                                            'border-[#d9cef3] bg-[#f3eefc] text-primary': team.is_favorite,
                                            'border-primary/15 text-[#9a94a8]': !team.is_favorite,
                                        }
                                    )}
                                    disabled={props.isDisabled}
                                    onClick={() => {
                                        if (props.onRequest) {
                                            return props.onRequest(team.is_favorite ? removeFromFavorites : addToFavorites)
                                        }
                                        return team.is_favorite ? removeFromFavorites() : addToFavorites()
                                    }}
                                >
                                    <i className={`fa-heart ${team.is_favorite ? 'fas' : 'far'}`} />
                                </button>
                            </Tooltip>
                        )}
                    </div>
                </div>

                {/* Identity area */}
                <div className="h-8 mt-1.5 flex flex-col justify-center">
                    {isAgency && (
                        <div className="flex items-center gap-1.5 text-[12.5px] text-gray-500">
                            <img src={team.supplier_client.logo_path} className="h-[22px] rounded" />
                            From
                            <Link to={`/agency-profiles/${team.supplier_client.id}`} className="flex items-center gap-2">
                                <span className="font-bold text-gray-900 hover:text-primary hover:underline">
                                    {team.supplier_client.name}
                                </span>
                            </Link>

                            {team.location && (
                                <>
                                    <span className="h-[3px] w-[3px] rounded-full bg-[#C9C2DC]"></span>
                                    <span className="">{team.location}</span>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Body */}
            <div className="min-h-[236px] grid grid-cols-[250px_1fr] max-md:grid-cols-1 border-t border-primary/10">
                {/* Match cell */}
                <div className="border-r max-md:border-r-0 max-md:border-b border-primary/10 flex flex-col items-center justify-center p-[18px] gap-3">
                    <CircleProgress
                        className="metric-circle w-[150px] self-center"
                        progress={team.overallMatchPercent()}
                        strokeWidth={7}
                    >
                        <p>
                            <span className="text-[29px] font-bold">{team.overallMatchPercent(true)}%</span>
                            <small className="text-[13px] font-semibold text-gray-600">Match</small>
                        </p>
                    </CircleProgress>

                    <button
                        type="button"
                        className="inline-flex items-center gap-1 bg-transparent border-none text-primary font-semibold text-[12.5px] cursor-pointer hover:underline"
                        onClick={showMatchDetails}
                    >
                        More <ChevronRight className="h-3 w-3" />
                    </button>
                </div>

                {/* Members cell */}
                <div className="p-4 px-[22px] flex flex-col min-w-0">
                    <div className="flex items-baseline gap-3 mb-3 min-h-[22px]">
                        <span className="text-gray-900 font-bold text-base tracking-wide">Team Members</span>
                        {team.positions?.length > 2 && (
                            <Link
                                to={teamProfileLink}
                                className="text-[13px] font-semibold text-primary hover:underline"
                            >
                                View all {team.positions.length} members
                            </Link>
                        )}
                    </div>

                    <div className="grid grid-cols-3 max-md:grid-cols-1 gap-4 flex-1 min-h-0">
                        {vips.slice(0, 3).map((user) => {
                            const filteredDisciplines = user.disciplines?.filter(d => !!d.name) || [];
                            return (
                                <MemberCard
                                    key={`${user.id}-${team.id}`}
                                    user={user}
                                    disciplines={filteredDisciplines}
                                    profileLink={memberProfilePath(user.id)}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="h-[66px] shrink-0 border-t border-primary/10 bg-[#fbfafd] flex items-center justify-between px-6">
                <div className="flex items-baseline gap-2">
                    <span className="text-[11px] text-gray-500 uppercase tracking-wide font-semibold">Average Standard Rate</span>
                    <span className="text-[17px] font-bold text-primary">
                        {team.rangedPlatformRates()}<small className="text-xs text-gray-500 font-semibold">/hr</small>
                    </span>
                </div>

                {/* <Link
                    to={teamProfileLink}
                    className="text-primary font-semibold text-[13.5px] hover:underline no-underline"
                >
                    View Team Details
                </Link> */}
                <Link
                    to={teamProfileLink}
                    className="bg-primary font-semiboldtext-[13.5px] no-underline text-white rounded-md px-[22px] py-[11px] inline-flex items-center"
                >
                    View Team <ChevronRight className="h-4 w-4 ml-1.5" />
                    {/* View Team → */}
                </Link>
            </div>
        </article>
    );
};

export default TeamTileHorizontalRedesign;


const MemberCard = ({ user, disciplines, profileLink }) => {
    return (
        <div className="bg-[#f6f5f9] border border-primary/10 rounded-xl p-4 flex items-start gap-3.5 min-w-0 transition hover:border-primary hover:bg-[#f4f1fb] hover:shadow-sm">
            <Link to={profileLink} className="shrink-0">
                {/* <UserImageCircle user={user} hideTooltip={true} size={56} className="img-grayscale" /> */}

                <UserImageCircleNEW
                    user={user}
                    className="!h-[56px] !w-[56px] img-grayscale"
                    showAvailabilityDot={false}
                    showOpenToWork={false}
                />
            </Link>

            <div className="flex-1 min-w-0">
                <TalentDisplayName talent={user}>
                    <h4 className="m-0 mb-0.5 text-[15px] font-bold leading-tight overflow-hidden text-ellipsis whitespace-nowrap">
                        {user.firstNamePlusInitial()}
                    </h4>
                </TalentDisplayName>

                <p className="text-xs text-gray-500 leading-snug mb-2 overflow-hidden text-ellipsis whitespace-nowrap">
                    {user.job_title}
                </p>

                <div className="flex flex-wrap gap-1.5 items-center">
                    {disciplines.slice(0, 2).map(d => (
                        <span
                            key={d.id}
                            className="text-[11px] py-[3px] px-2 rounded-md border border-primary/15 bg-white text-primary font-semibold whitespace-nowrap overflow-hidden text-ellipsis max-w-full"
                        >
                            {d.name}
                        </span>
                    ))}
                    {disciplines.length > 2 && (
                        <span className="text-[11px] py-[3px] px-2 rounded-md border border-primary/15 border-dashed bg-transparent text-gray-500 font-semibold cursor-pointer hover:text-primary hover:border-primary">
                            +{TB.pluralize('role', disciplines.length - 2, true)}
                        </span>
                    )}
                </div>

                <Link
                    to={profileLink}
                    className="mt-2.5 inline-flex items-center gap-1 text-[12.5px] font-semibold text-primary hover:underline no-underline"
                >
                    View Profile <ChevronRight className="h-3 w-3" />
                </Link>
            </div>
        </div>
    );
};


export const TeamTileHorizontalRedesignSkeleton = () => {
    return (
        <article className="relative bg-white border border-primary/15 rounded-[14px] shadow-sm hover:shadow-md overflow-hidden flex flex-col transition-shadow duration-150">

            <div className="min-h-[116px] px-6 pt-[18px] flex flex-col gap-2 mb-1.5 bg-white">
                <div className="skeleton h-[17px] w-[170px]" />
                <div className="skeleton h-[30px] w-[180px]" />
            </div>


            <div className="min-h-[236px] grid grid-cols-[250px_1fr] max-md:grid-cols-1 border-t border-primary/10">
                {/* Match cell */}
                <div className="border-r max-md:border-r-0 max-md:border-b border-primary/10 flex flex-col items-center justify-center p-[18px] gap-3">

                    <div className="skeleton h-[150px] w-[150px] rounded-full " />

                    <div className="py-3">
                        <div className="skeleton h-[12px] w-[65px]" />
                    </div>
                </div>

                {/* Members cell */}
                <div className="p-4 px-[22px] flex flex-col min-w-0">
                    <div className="flex items-baseline gap-3 mb-3 min-h-[22px]">
                        <div className="skeleton w-[135px] h-6" />

                    </div>

                    <div className="grid grid-cols-3 max-md:grid-cols-1 gap-4 flex-1 min-h-0">

                        {Array.from({ length: 3 }, () => (
                            <div className="bg-[#f6f5f9] border border-primary/10 rounded-xl p-4 flex items-start gap-3.5 min-w-0">
                                <div className="skeleton h-[56px] w-[56px] rounded-full flex items-center justify-center" />

                                <div className="flex-1 min-w-0">
                                    <div className="skeleton h-4 w-[90px] mb-0.5" />
                                    <div className="skeleton h-[14px] w-[125px] mt0.5" />


                                    <div className="flex flex-wrap gap-1.5 items-center pt-3">
                                        <div className="skeleton h-5 w-[160px] mb0.5" />
                                        <div className="skeleton h-5 w-[138px] mb0.5" />
                                    </div>

                                    <div className="py-3">
                                        <div className="skeleton h-[12px] w-[95px]" />
                                    </div>
                                </div>

                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="h-[66px] shrink-0 border-t border-primary/10 bg-[#fbfafd] flex items-center justify-between px-6">
                <div className="flex items-baseline gap-2">
                    <div className="skeleton h-[18px] w-[185px]" />
                </div>

                <div className="skeleton h-[44px] w-[145px]" />
            </div>
        </article>
    )
}
